/**
 * "Save as block…": saves the chosen blocks (blocks/teamBlocks.ts) to the team library, either as a
 * new block or replacing an existing block's content. The source template and the version the
 * editor opened go along so the server can copy the images the blocks use.
 */
import { useEffect, useState } from 'react'
import { Button, Modal } from '../ui'
import BlockDetailsFields, {
  emptyBlockDetails, fieldStyle, labelStyle, toBlockDetails, validateBlockDetails, type BlockDetailsValue,
} from '../blocks/BlockDetailsFields'
import { blockContentJson, type BlockRange } from '../../blocks/teamBlocks'
import type { EditorDocument } from '../../cdm/fromCdm'
import { blocksApiService, type ContentBlockSummary } from '../../services/blocksApiService'
import { ApiError } from '../../services/BaseApiService'
import { colors, fontSize, radius, spacing } from '../../styles/theme'

interface Props {
  range: BlockRange
  meta: Omit<EditorDocument, 'pmDoc'>
  templateId: string
  templateVersionId: string | null
  onClose: () => void
  onSaved: (message: string) => void
}

type Mode = 'new' | 'update'

export default function SaveBlockModal({ range, meta, templateId, templateVersionId, onClose, onSaved }: Props) {
  const [mode, setMode] = useState<Mode>('new')
  const [details, setDetails] = useState<BlockDetailsValue>(emptyBlockDetails)
  const [blocks, setBlocks] = useState<ContentBlockSummary[]>([])
  const [targetId, setTargetId] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    blocksApiService.list()
      .then(b => { if (!cancelled) setBlocks(b) })
      .catch(() => { /* no picker, no suggestions — a new block still saves */ })
    return () => { cancelled = true }
  }, [])

  function pick(id: string) {
    setTargetId(id)
    const b = blocks.find(x => x.contentBlockId === id)
    if (b) setDetails({ name: b.name, description: b.description ?? '', category: b.category ?? '', tags: b.tags ?? [] })
  }

  async function save() {
    if (mode === 'update' && !targetId) { setError('Choose the block to replace.'); return }
    const invalid = validateBlockDetails(details)
    if (invalid) { setError(invalid); return }
    let contentJson: string
    try {
      contentJson = blockContentJson(range, meta)
    } catch {
      setError('This selection could not be converted into a block.')
      return
    }
    const input = {
      ...toBlockDetails(details),
      contentJson,
      sourceTemplateId: templateId,
      sourceTemplateVersionId: templateVersionId ?? undefined,
    }
    setSaving(true)
    setError('')
    try {
      if (mode === 'new') await blocksApiService.create(input)
      else await blocksApiService.update(targetId, input)
      onSaved(mode === 'new' ? `Saved “${input.name}” to team blocks.` : `Updated “${input.name}” in team blocks.`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Saving the block failed.')
      setSaving(false)
    }
  }

  const count = range.nodes.length
  const radio = (m: Mode, label: string) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: fontSize.sm, color: colors.text, cursor: 'pointer' }}>
      <input type="radio" name="save-block-mode" checked={mode === m} onChange={() => { setMode(m); setError(''); if (m === 'new') { setTargetId(''); setDetails(emptyBlockDetails()) } }} />
      {label}
    </label>
  )

  return (
    <Modal
      open
      onClose={onClose}
      title="Save as block"
      subtitle={`${count} block${count === 1 ? '' : 's'} from this template, saved to your team's library.`}
      width={480}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={save} loading={saving}>{mode === 'new' ? 'Save block' : 'Replace content'}</Button>
        </>
      }
    >
      <form onSubmit={e => { e.preventDefault(); save() }} style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        <div role="radiogroup" aria-label="Save as" style={{ display: 'flex', gap: spacing.md }}>
          {radio('new', 'New block')}
          {radio('update', 'Update an existing block')}
        </div>
        {mode === 'update' && (
          <label style={labelStyle}>
            Block to replace
            <select aria-label="Block to replace" value={targetId} onChange={e => pick(e.target.value)} style={fieldStyle}>
              <option value="">Choose a block…</option>
              {blocks.filter(b => !b.archived).map(b => <option key={b.contentBlockId} value={b.contentBlockId}>{b.name}</option>)}
            </select>
            <span style={{ fontSize: fontSize.xs, color: colors.textMuted }}>Its content is replaced with this selection. Templates that already use it keep their copy.</span>
          </label>
        )}
        <BlockDetailsFields value={details} onChange={setDetails} categories={blocks.map(b => b.category ?? '')} autoFocus />
        {error && (
          <div role="alert" style={{ background: colors.errorLight, color: colors.error, borderRadius: radius.sm, padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm }}>{error}</div>
        )}
        <button type="submit" hidden />
      </form>
    </Modal>
  )
}
